import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { isObject } from 'underscore';
import classNames from 'classnames';

import './range-input.css';

interface IRangeProps {
  defaultValue?: number;
  value?: number;
  min?: number;
  max?: number;
  onChange?: (value: number) => void;
  inheritWidth?: boolean;
  onStart?: () => void;
  onProgress?: (value: number) => void;
  /** overrides the default gray gradient track - fill is the progressed portion, track is the remainder */
  fillColor?: string;
  trackColor?: string;
  thumbColor?: string;
}

const RangeInput = ({
  min = 0,
  max = 100,
  defaultValue = 20,
  value: inputValue,
  onChange,
  onProgress,
  onStart,
  inheritWidth,
  fillColor,
  trackColor,
  thumbColor,
}: IRangeProps) => {
  const defaultProgress = useMemo(() => {
    return (defaultValue / max) * 100;
  }, [defaultValue, max]);

  const [currentProgress, setCurrentProgress] = useState(defaultProgress);
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    if (typeof inputValue !== 'undefined') {
      setValue(inputValue);
      const inputMax = max;

      const updatedProgress = (inputValue / inputMax) * 100;
      setCurrentProgress(updatedProgress);
    }
  }, [inputValue, max]);

  const handleChange = (event: unknown) => {
    if (!isObject(event)) return;

    const sliderValue = Number(event?.target?.value ?? 0);
    setValue(sliderValue);

    const inputMax = event?.target?.max ?? max;

    const updatedProgress = (sliderValue / inputMax) * 100;
    setCurrentProgress(updatedProgress);
    return sliderValue;
  };

  const handleInput = (event: unknown) => {
    const sliderValue = handleChange(event);
    if (typeof sliderValue !== 'undefined') {
      // if there is a progress handler trigger that, otherwise trigger onChange
      if (onProgress) {
        onProgress(sliderValue);
      } else {
        onChange?.(sliderValue);
      }
    }
  };

  const handleFinish = (event: unknown) => {
    const sliderValue = handleChange(event);

    // only trigger this if there is an onProgress callback
    if (typeof sliderValue !== 'undefined' && onProgress) {
      onChange?.(sliderValue);
    }
  };

  return (
    <div className={classNames('range-input', { 'inherit-width': inheritWidth })}>
      <input
        className="range-input-input"
        onInput={handleInput}
        onPointerDown={onStart}
        onPointerUp={handleFinish}
        type="range"
        min={min}
        max={max}
        value={value}
        style={
          {
            '--range-input-progress': `${currentProgress}%`,
            ...(fillColor ? { '--range-input-fill-color': fillColor } : {}),
            ...(trackColor ? { '--range-input-track-color': trackColor } : {}),
            ...(thumbColor ? { '--range-input-thumb-color': thumbColor } : {}),
          } as CSSProperties
        }
      />
    </div>
  );
};

export default RangeInput;
